// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { AppIcon } from '@shared/ui-kit/components/app-icon';
import { classNames } from '@shared/ui-kit/utils/classnames';
import {
  type FC,
  type FocusEventHandler,
  type KeyboardEventHandler,
  type MouseEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { NumericFormat } from 'react-number-format';

export interface InputPercentageProps {
  value: number;
  className?: string;
  onChange?: (value: number | null) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  max?: number;
  step?: number;
  precision?: number;
  onKeyPress?: KeyboardEventHandler<HTMLInputElement>;
  controls?: boolean;
  totalWeight?: number;
}

export const InputPercentage: FC<InputPercentageProps> = ({
  value,
  max = 100,
  step = 0.5,
  onKeyPress,
  onBlur,
  onChange,
  precision,
  className,
  controls = true,
  totalWeight,
  ...props
}) => {
  const [localValue, setLocalValue] = useState<number>(value);

  const handleValueChange = useCallback(
    ({ floatValue }: { floatValue: number | undefined }) => {
      const newValue = floatValue ?? 0;
      onChange?.(newValue);
      setLocalValue(newValue);
    },
    [onChange],
  );

  const handleBlur = useCallback<FocusEventHandler<HTMLInputElement>>(() => {
    /* istanbul ignore next */
    if (totalWeight === 100 && localValue > value) {
      setLocalValue(value);
    }
  }, [localValue, totalWeight, value]);

  const onStep: MouseEventHandler<HTMLButtonElement> = useCallback(
    event => {
      const step = parseFloat(event.currentTarget.value);

      const newValue = value + step;

      onChange?.(newValue);
    },
    [onChange, value],
  );

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <div className="relative select-none">
      <NumericFormat
        value={localValue}
        min={0}
        max={max}
        isAllowed={({ value }) => {
          return Number(value) <= max;
        }}
        decimalScale={precision}
        allowNegative={false}
        decimalSeparator="."
        suffix="%"
        className={classNames('input-common', className, '!money-display')}
        data-testid="input-percentage"
        onValueChange={handleValueChange}
        onKeyDown={onKeyPress}
        onBlur={handleBlur}
        {...props}
      />
      <div
        className="absolute right-3 top-2 flex flex-col aria-hidden:hidden"
        data-testid="input-percentage-controls"
        aria-hidden={!controls}
      >
        <button
          type="button"
          data-testid="increment"
          onClick={onStep}
          className="text-sidebar-text hover:text-primary"
          value={step}
          aria-controls="input-percentage"
          disabled={value === max}
        >
          <AppIcon
            className="select-none"
            icon="CaretUp"
            color="inherit"
            size="sm"
          />
        </button>
        <button
          type="button"
          data-testid="decrement"
          onClick={onStep}
          className="text-sidebar-text hover:text-primary"
          value={-step}
          aria-controls="input-percentage"
          disabled={value === 0}
        >
          <AppIcon
            className="select-none"
            icon="CaretDown"
            color="inherit"
            size="sm"
          />
        </button>
      </div>
    </div>
  );
};
